import { Component, inject, computed, OnInit, OnDestroy, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Features, SalesforceChatFeatures } from './global-config.model';
import { UiSectionHeaderComponent } from '../../shared/components/ui-section-header/ui-section-header.component';
import { UiModalComponent } from '../../shared/components/ui-modal/ui-modal.component';
import { MatIconModule } from '@angular/material/icon';
import { TranslateModule } from '@ngx-translate/core';
import { GlobalStore } from './global.store';

@Component({
  selector: 'app-global',
  imports: [
    UiSectionHeaderComponent,
    UiModalComponent,
    MatIconModule,
    TranslateModule,
    FormsModule,
  ],
  providers: [GlobalStore],
  templateUrl: './global.component.html',
  styleUrl: './global.component.scss',
})
export class GlobalComponent implements OnInit, OnDestroy {
  private readonly store = inject(GlobalStore);
  private saveListener?: (event: Event) => void;

  // Public computed for template
  readonly config = computed(() => this.store.getCurrentConfig());
  readonly isLoading = computed(() => this.store.globalConfigResource.isLoading());
  readonly error = computed(() => this.store.globalConfigResource.error());

  // Delegation methods
  onDeliveriesRefreshChange(value: number): void {
    this.store.updateDeliveriesRefresh(value);
  }

  onMaxDaysForEvalChange(value: number): void {
    this.store.updateMaximumDaysForEval(value);
  }

  onMaxCoForUcChange(value: number): void {
    this.store.updateMaximumCoForUcActivate(value);
  }

  onTicketDisplayTimeChange(value: string): void {
    this.store.updateTicketDisplayTime(value);
  }

  onFeatureChange(event: { feature: keyof Features; enabled: boolean }): void {
    this.store.updateFeature(event.feature, event.enabled);
  }

  onSalesforceChatFeatureChange(event: {
    ticketType: keyof SalesforceChatFeatures;
    enabled: boolean;
  }): void {
    this.store.updateSalesforceChatFeature(event.ticketType, event.enabled);
  }

  onFunctionTitlesChange(titles: string[]): void {
    this.store.updateFunctionTitles(titles);
  }

  onDeleteFunctionTitle(index: number): void {
    const current = this.config();
    if (current) {
      const newTitles = current.functionTitles.filter((_, i) => i !== index);
      this.store.updateFunctionTitles(newTitles);
    }
  }

  isAddFunctionTitleModalOpen = signal(false);
  newFunctionTitle = signal('');

  onAddFunctionTitle(): void {
    this.newFunctionTitle.set('');
    this.isAddFunctionTitleModalOpen.set(true);
  }

  onConfirmAddFunctionTitle(): void {
    const title = this.newFunctionTitle().trim();
    if (title) {
      const current = this.store.getCurrentConfig();
      if (current) {
        const updatedTitles = [...current.functionTitles, title];
        this.store.updateFunctionTitles(updatedTitles);
      }
    }
    this.isAddFunctionTitleModalOpen.set(false);
  }

  onCancelAddFunctionTitle(): void {
    this.isAddFunctionTitleModalOpen.set(false);
  }

  onSave(): void {
    this.store.saveConfig();
  }

  ngOnInit(): void {
    this.saveListener = () => {
      this.onSave();
    };
    window.addEventListener('global-save-triggered', this.saveListener);
  }

  ngOnDestroy(): void {
    if (this.saveListener) {
      window.removeEventListener('global-save-triggered', this.saveListener);
    }
  }
}
